import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Send, FileText, ShieldAlert, Info, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { BRAND } from '../../mock/mock';

const defaultMessage = (name) => `Dear ${name},

Thank you for your interest in becoming a ${BRAND.name} recruitment partner. We are pleased to move forward with your application.

Please find the partnership agreement attached. Kindly review the terms and sign electronically. Once signed, we will activate your recruiter portal account and send your login credentials.

Best regards,
${BRAND.name} Partnerships Team`;

const SendAgreementModal = ({ recruiter, dsStatus, onClose, onSent }) => {
  const { toast } = useToast();
  const isResend = !!(recruiter._resend || recruiter.docusign_envelope_id || recruiter.agreement_sent_at);
  const [form, setForm] = useState({
    to: recruiter.email,
    subject: `${BRAND.name} Recruitment Agreement${isResend ? ' (Updated)' : ''}`,
    message: defaultMessage(recruiter.name),
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') { toast({ title: 'PDF only', description: 'Please upload a PDF file.' }); return; }
    if (f.size > 20 * 1024 * 1024) { toast({ title: 'Too large', description: 'Max 20MB.' }); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast({ title: 'PDF required' }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('to', form.to);
      fd.append('subject', form.subject);
      fd.append('message', form.message);
      fd.append('agreement_pdf', file);
      const { data } = await api.post(`/admin/recruiters/${recruiter.id}/send-agreement`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.method === 'docusign') {
        toast({ title: isResend ? 'Updated agreement sent via DocuSign' : 'Agreement sent via DocuSign', description: `Envelope ${data.envelope_id} delivered to ${form.to}` });
      } else {
        toast({ title: isResend ? 'Updated agreement emailed (fallback)' : 'Agreement emailed (fallback)', description: `DocuSign unavailable — PDF was emailed to ${form.to} directly.` });
      }
      onSent?.();
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || 'Unable to send agreement.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl z-[61]">
        <div className="sticky top-0 bg-white z-10 border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">{isResend ? 'Resend Partnership Agreement' : 'Send Partnership Agreement'}</div>
            <h3 className="font-display text-xl text-[#0B5E2A]">{recruiter.name}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="send-agreement-close" className="p-2 rounded hover:bg-[#f0f6ee]">
            <X className="h-5 w-5 pointer-events-none" />
          </button>
        </div>

          <form onSubmit={submit} className="p-6 space-y-4">
            {isResend && (
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-3 text-sm">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">This will resend (and supersede) the previous agreement.</div>
                  <div className="mt-0.5">The recruiter will receive a brand-new envelope / email. Any prior signing request for this recruiter will be invalidated. The recruiter's status will be reset to <em>Agreement Sent</em>.</div>
                </div>
              </div>
            )}
            {dsStatus && !dsStatus.ready && (
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-3 text-sm">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  DocuSign is not ready (consent required or API error). If unavailable, the PDF will be emailed directly to the recruiter via Resend as a fallback.
                  {dsStatus.consent_url && <a href={dsStatus.consent_url} target="_blank" rel="noreferrer" className="ml-1 font-semibold underline">Grant consent</a>}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">To</label>
              <input name="to" type="email" value={form.to} onChange={onChange} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 bg-[#f6faf3]" />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Subject</label>
              <input name="subject" value={form.subject} onChange={onChange} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Message</label>
              <textarea name="message" value={form.message} onChange={onChange} rows={7} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 resize-y" />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Agreement Document (PDF) <span className="text-red-600">*</span></label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                role="button"
                tabIndex={0}
                data-testid="agreement-upload-dropzone"
                className="block border-2 border-dashed border-[#0B5E2A]/25 rounded-xl p-6 text-center cursor-pointer bg-[#f5f9f3] hover:bg-[#edf6ea] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
              >
                <Upload className="h-6 w-6 brand-green mx-auto pointer-events-none" />
                {!file ? (
                  <>
                    <div className="mt-2 font-semibold text-[#0B5E2A] text-sm pointer-events-none">Upload the agreement PDF for this recruiter</div>
                    <span className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0B5E2A] text-white text-sm font-semibold pointer-events-none">
                      <Upload className="h-4 w-4" /> Choose PDF
                    </span>
                  </>
                ) : (
                  <div className="mt-2 flex items-center justify-center gap-2 text-sm pointer-events-none">
                    <FileText className="h-4 w-4 brand-green" />
                    <span className="font-semibold text-[#0B5E2A]">{file.name}</span>
                    <span className="text-xs text-[#6b7e6d]">({Math.ceil(file.size / 1024)} KB)</span>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="agreement-file-input" />
              {file && (
                <button type="button" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="mt-2 text-xs font-semibold text-[#9b1c1c] hover:underline">
                  Remove file
                </button>
              )}
            </div>

            <button type="button" onClick={() => setShowHelper(!showHelper)} className="text-sm font-semibold text-[#1e40af] hover:underline inline-flex items-center gap-1.5">
              <Info className="h-4 w-4" /> How to add signature fields to your PDF
            </button>
            {showHelper && (
              <div className="bg-[#f0f6ee] border border-[#0B5E2A]/15 rounded-lg p-4 text-sm text-[#2e4030] space-y-2">
                <p>DocuSign auto-places fields wherever it finds these <strong>anchor tags</strong> in your PDF text. Type the tag exactly (slashes included) where you want each field, then export the document as a PDF.</p>
                <div className="mt-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#6b7e6d] mb-1.5">Auto-filled by DocuSign (signer identity)</div>
                  <ul className="space-y-1 pl-5 list-disc">
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/sn1/</code> — Signature <span className="text-[#6b7e6d]">(required)</span></li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/in1/</code> — Initials</li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/n1/</code> — Full Name</li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/d1/</code> — Date Signed (auto timestamp)</li>
                  </ul>
                </div>
                <div className="mt-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#6b7e6d] mb-1.5">Typed by the recruiter at signing</div>
                  <ul className="space-y-1 pl-5 list-disc">
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/co1/</code> — Company Name</li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/ti1/</code> — Designation / Title</li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/dt1/</code> — Custom Date (free-form)</li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/em1/</code> — Email Address</li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/ph1/</code> — Phone Number</li>
                    <li><code className="bg-white px-1.5 py-0.5 rounded border text-xs">/ad1/</code> — Address</li>
                  </ul>
                </div>
                <p className="text-xs text-[#6b7e6d] pt-1">Tip: color the anchor text white so it's invisible on the final PDF but DocuSign can still detect it. Use unique tags (e.g. <code>/sn2/</code>, <code>/co2/</code>) only if you add a second signer later.</p>
              </div>
            )}

            <div className="bg-[#DEE9FA] text-[#1e40af] rounded-lg p-3 text-sm flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>The uploaded agreement will be sent via DocuSign for the recruiter to review and sign electronically.</span>
            </div>

            <div className="flex gap-2 pt-2 border-t border-[#0B5E2A]/10">
              <button type="button" onClick={onClose} data-testid="send-agreement-cancel" className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee] transition-colors">Cancel</button>
              <button type="submit" disabled={busy || !file} data-testid="send-agreement-submit" className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4 pointer-events-none" /> {isResend ? 'Resend Agreement' : 'Send Agreement'}</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
  );
};

export default SendAgreementModal;
